import React, { createContext, useContext, useState, useEffect } from 'react';
import { useStore } from './StoreContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { settings } = useStore();

  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('himalayan_cart');
    return saved ? JSON.parse(saved) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedProductModal, setSelectedProductModal] = useState(null);

  useEffect(() => {
    localStorage.setItem('himalayan_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, selectedVariant, quantity = 1) => {
    const variantToUse = selectedVariant || product.variants[0];
    const cartItemId = `${product.id}-${variantToUse.weightEn || variantToUse.weight}`;

    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => item.cartItemId === cartItemId);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      } else {
        return [
          ...prev,
          {
            cartItemId,
            productId: product.id,
            nameUrdu: product.nameUrdu,
            nameEn: product.nameEn,
            image: product.image,
            category: product.category,
            variant: variantToUse,
            quantity: quantity,
            unitPrice: variantToUse.price
          }
        ];
      }
    });

    setIsCartOpen(true);
  };

  const updateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const totalItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.unitPrice * item.quantity,
    0
  );

  const freeDeliveryThreshold = settings?.freeDeliveryThreshold || 5000;
  const standardDeliveryFee = settings?.deliveryFee || 200;

  const isFreeDelivery = subtotal >= freeDeliveryThreshold && subtotal > 0;
  const deliveryFee = cartItems.length === 0 ? 0 : isFreeDelivery ? 0 : standardDeliveryFee;
  const totalAmount = subtotal + deliveryFee;
  const amountForFreeDelivery = Math.max(0, freeDeliveryThreshold - subtotal);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const viewProductDetail = (prod) => {
    setSelectedProduct(prod);
    if (typeof window !== 'undefined' && prod?.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('product', prod.id);
      window.history.pushState({}, '', url);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        totalItemsCount,
        subtotal,
        deliveryFee,
        totalAmount,
        isFreeDelivery,
        amountForFreeDelivery,
        freeDeliveryThreshold,
        isCartOpen,
        setIsCartOpen,
        isCheckoutOpen,
        setIsCheckoutOpen,
        selectedProductModal,
        setSelectedProductModal,
        selectedProduct,
        setSelectedProduct,
        viewProductDetail
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
